/* eslint-disable max-lines */
import { PointSymbol } from "@evergis/sgis/es/symbols/point/Point";
import { StaticImageSymbol } from "@evergis/sgis/es/symbols/point/StaticImageSymbol";
import { SquareSymbol } from "@evergis/sgis/es/symbols/point/Square";
import { MaskedImage } from "@evergis/sgis/es/symbols/point/MaskedImage";

import { palette } from "@evergis/ui";
import { SimplePolylineSymbol } from "@evergis/react";
import { LineEndingType } from "@evergis/api";

import { getDashStylePreset, StrokeStyle, toIntervals } from "./strokeStyle";
import { getHexColor } from "../../../utils/color";
import { SGisBrushFill } from "../SGisBrushFill";
import { PolygonSymbol } from "../../../interfaces/style";
import { SGisPolylineSymbol } from "../SGisPolylineSymbol";
import { SGisPolygonSymbol } from "../SGisPolygonSymbol";

import pinImage from "./Masks/pin.png";
import homeImage from "./Masks/home.png";
import businessImage from "./Masks/business.png";
import mallImage from "./Masks/mall.png";
import humanImage from "./Masks/human.png";
import letterImage from "./Masks/letter.png";
import settingsImage from "./Masks/settings.png";
import flagImage from "./Masks/flag.png";
import starImage from "./Masks/star.png";
import busImage from "./Masks/bus.png";
import carImage from "./Masks/car.png";
import messageImage from "./Masks/message.png";
import parkingImage from "./Masks/parking.png";
import successImage from "./Masks/success.png";
import errorImage from "./Masks/error.png";
import warningImage from "./Masks/warning.png";
import circleBack from "./Masks/circle_back.png";
import pinBack from "./Masks/pin_back.png";
import cow from "./cow.png";

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const DEFAULT_SCALING_FACTOR = [5, 5];

/**
 * Дефолтные символы
 * http://cf.everpoint.ru/pages/viewpage.action?pageId=45024217
 */

export const BasicSymbols = {
  round: new PointSymbol({
    size: 8,
    strokeWidth: 1,
    fillColor: "rgba(0, 163, 245, 0.6)",
    strokeColor: "rgba(255, 255, 255, 0.6)",
  }),
  square: new SquareSymbol({
    size: 12,
    strokeWidth: 1,
    fillColor: "rgba(0, 163, 245, 0.6)",
    strokeColor: "rgba(255, 255, 255, 0.6)",
  }),
  polygon: new SGisPolygonSymbol({
    type: "polygonSymbol",
    stroke: {
      type: "solid",
      color: getHexColor("rgba(0, 130, 194, 0.65)"),
      width: 1,
    },
    fill: {
      type: "solid",
      color: getHexColor("rgba(0, 192, 251, 0.63)"),
    },
  }),
  line: new SGisPolylineSymbol({
    type: "simplePolylineSymbol",
    stroke: {
      type: "solid",
      color: "#ff48e3ff",
      width: 2,
    },
    beginning: null,
    ending: null,
  }),
} as const;

export const ExtraSymbols = {
  dashLine: new SGisPolylineSymbol({
    type: "simplePolylineSymbol",
    stroke: {
      type: "dashed",
      color: "#00afffff",
      width: 2,
      intervals: toIntervals(getDashStylePreset(StrokeStyle.Dot).intervals, 2),
    },
    beginning: {
      type: LineEndingType.None,
      size: 7,
    },
    ending: {
      type: LineEndingType.Arrow,
      size: 7,
    },
  } as unknown as SimplePolylineSymbol),
  outlinePolygon: new SGisPolygonSymbol({
    type: "polygonSymbol",
    stroke: {
      type: "dashed",
      intervals: toIntervals(getDashStylePreset(StrokeStyle.Dash).intervals, 1),
      color: getHexColor("rgba(0, 130, 194, 0.65)"),
      width: 1,
    },
    fill: {
      type: "solid",
      color: getHexColor("rgba(0, 192, 251, 0.63)"),
    },
  } as unknown as PolygonSymbol),
  brushFillXY1: new SGisBrushFill({
    type: "polygonSymbol",
    fill: {
      type: "hatch",
      hatchAngle: Math.PI / 4,
      hatchWidth: 1,
      hatchColor: getHexColor("rgba(33, 126, 0, 0.65)"),
      scalingFactor: DEFAULT_SCALING_FACTOR,
      color: getHexColor("rgba(0, 251, 16, 0.63)"),
    },
    stroke: {
      type: "solid",
      color: getHexColor("rgba(33, 126, 0, 0.65)"),
      width: 1,
    },
  }),
  brushFillXY2: new SGisBrushFill({
    type: "polygonSymbol",
    fill: {
      type: "hatch",
      hatchAngle: (3 * Math.PI) / 4,
      hatchWidth: 1,
      hatchColor: getHexColor("rgba(33, 126, 0, 0.65)"),
      scalingFactor: DEFAULT_SCALING_FACTOR,
      color: getHexColor("rgba(0, 251, 16, 0.63)"),
    },
    stroke: {
      type: "solid",
      color: getHexColor("rgba(33, 126, 0, 0.65)"),
      width: 1,
    },
  }),
  brushFillX: new SGisBrushFill({
    type: "polygonSymbol",
    fill: {
      type: "hatch",
      hatchAngle: 0,
      hatchWidth: 1,
      hatchColor: getHexColor("rgba(0, 130, 194, 0.646937)"),
      scalingFactor: DEFAULT_SCALING_FACTOR,
      color: getHexColor("rgba(0, 192, 251, 0.629472)"),
    },
    stroke: {
      type: "solid",
      color: getHexColor("rgba(0, 130, 194, 0.646937)"),
      width: 1,
    },
  }),
  brushFillY: new SGisBrushFill({
    type: "polygonSymbol",
    fill: {
      type: "hatch",
      hatchAngle: Math.PI / 2,
      hatchWidth: 1,
      hatchColor: getHexColor("rgba(130, 130, 130, 0.646937)"),
      scalingFactor: DEFAULT_SCALING_FACTOR,
      color: getHexColor("rgba(0, 0, 0, 0.167559)"),
    },
    stroke: {
      type: "solid",
      color: getHexColor("rgba(130, 130, 130, 0.646937)"),
      width: 1,
    },
  }),
};

export const MaskedSymbols = {
  pin: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: pinBack,
    maskSource: pinImage,
    maskColor: palette.primaryDeep,
  }),
  home: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: homeImage,
    maskColor: palette.primaryDeep,
  }),
  business: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: businessImage,
    maskColor: palette.primaryDeep,
  }),
  mall: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: mallImage,
    maskColor: palette.primaryDeep,
  }),
  human: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: humanImage,
    maskColor: palette.primaryDeep,
  }),
  letter: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: letterImage,
    maskColor: palette.primaryDeep,
  }),
  settings: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: settingsImage,
    maskColor: palette.primaryDeep,
  }),
  flag: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: flagImage,
    maskColor: palette.primaryDeep,
  }),
  star: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: starImage,
    maskColor: palette.primaryDeep,
  }),
  bus: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: busImage,
    maskColor: palette.primaryDeep,
  }),
  car: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: carImage,
    maskColor: palette.primaryDeep,
  }),
  message: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: messageImage,
    maskColor: palette.primaryDeep,
  }),
  parking: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: parkingImage,
    maskColor: palette.primaryDeep,
  }),
  success: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: successImage,
    maskColor: "#00bc56",
  }),
  error: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: errorImage,
    maskColor: palette.error,
  }),
  warning: new MaskedImage({
    width: 24,
    height: 24,
    anchorPoint: [12, 12],
    imageSource: circleBack,
    maskSource: warningImage,
    maskColor: palette.secondary,
  }),
  geocodePin: new MaskedImage({
    width: 32,
    height: 32,
    anchorPoint: [12, 12],
    imageSource: pinBack,
    maskSource: pinImage,
    maskColor: "#FF4E00",
  }),
  geoLocationPin: new MaskedImage({
    width: 32,
    height: 32,
    anchorPoint: [12, 12],
    imageSource: pinBack,
    maskSource: pinImage,
    maskColor: "#9013FE",
  }),
};

export const StaticSymbol = new StaticImageSymbol({
  width: 24,
  height: 24,
  source: cow,
  anchorPoint: [12, 12],
});
